import { faComment } from '@fortawesome/free-solid-svg-icons/faComment'
import { faStar } from '@fortawesome/free-solid-svg-icons/faStar'
import React, { useState, useRef } from 'react'
import { Simple } from '../Avatar'
import * as Navigation from '../Navigation'
import { concatStyles } from 'utils'
import { Name } from './Name'
import { Subtitle } from './Subtitle'
import { View } from './View'

export interface ExtendedProfile {
  createdAt?: string
  color?: string
  avatarUrl?: string
  // removed `about`
  keywords?: string[]
  hasUnreadMessages?: boolean
  title: string
  name: string
  id: string
}

export interface ExtendedProfileCardProps {
  profile: ExtendedProfile
  favorite?: boolean
  alert?: boolean
  matchDescription: string
  matchPercent: number
  onSelectProfile?: (profileId: string, opts?: { messages?: boolean }) => void
  onFavoriteProfile?: (id: string, fav: boolean) => Promise<any>
  renderAccessory?: () => JSX.Element | null
  className?: string
}

const PROFILE_CARD_STYLE =
  'relative bg-contentareabackground rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow'

export const ExtendedProfile: React.FC<ExtendedProfileCardProps> = ({
  favorite,
  profile,
  matchDescription,
  matchPercent,
  onSelectProfile,
  onFavoriteProfile: handleFavorite,
  renderAccessory,
  className,
}) => {
  const [favoriting, setFavoriting] = useState(false)
  const ignoreClick = useRef(false)

  const onCardClick = (e?: React.MouseEvent) => {
    if (ignoreClick.current) {
      ignoreClick.current = false
      return
    }
    onSelectProfile?.(profile.id)
  }

  const onFavClick = async (e?: React.MouseEvent) => {
    if (e) e.stopPropagation()
    ignoreClick.current = true
    if (!handleFavorite) return
    setFavoriting(true)
    await handleFavorite(profile.id, !favorite)
    setFavoriting(false)
  }

  const onMsgClick = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation()
    ignoreClick.current = true
    onSelectProfile?.(profile.id, { messages: true })
  }

  return (
    <View
      className={concatStyles([
        PROFILE_CARD_STYLE,
        className,
        'flex p-0 sm:flex-row items-top',
      ])}
      onClick={onCardClick}
    >
      {/* % badge */}
      <div className="absolute top-0.5 right-0.5 bg-amber-400 text-amber-900 text-xxs font-semibold rounded-sm px-2 py-1">
        {matchPercent}%
      </div>

      {/* left icons */}
      <div className="absolute top-3 left-3 flex space-x-2">
        {renderAccessory?.()}
      </div>

      {/* right icons */}
      <div className="absolute top-1 right-16 flex space-x-2">
        {profile.hasUnreadMessages && (
          <Navigation.IconButton
            appearance="accent"
            icon={faComment}
            onClick={onMsgClick}
          />
        )}
        {handleFavorite && (
          <Navigation.IconButton
            icon={faStar}
            onClick={onFavClick}
            appearance={favorite ? 'accent' : undefined}
            loading={favoriting}
          />
        )}
      </div>

      {/* avatar */}
      <div className="flex-shrink-0 mt-0">
        <Simple
          color={profile.color}
          name={profile.name}
          maxInitials={2}
          imageUrl={profile.avatarUrl}
          size={48}
        />
      </div>

      {/* main info */}
      <div className="flex-grow grid grid-cols-2">
        <div className="mt-4 sm:mt-0 sm:ml-4 text-center sm:text-left flex flex-col">
          <h3 className="font-body text-sm">{profile.name}</h3>
          <h4 className="font-body text-xs">{profile.title}</h4>

          {profile.keywords && profile.keywords.length > 0 && (
            <div className="mt-2">
              <h5 className="text-xxs font-semibold uppercase text-primary mb-0">
                Match Keywords
              </h5>
              <p className="mt-0 text-xxs text-gray-600 lowercase">
                {profile.keywords.join(', ')}
              </p>
            </div>
          )}
        </div>
        <div className="p-2 -m-2 bg-stone-100 flex">
          <p className="my-auto mx-4 text-xxs text-gray-700 italic font-body font-medium">
            {matchDescription}
          </p>
        </div>
      </div>
    </View>
  )
}

ExtendedProfile.displayName = 'Card.ExtendedProfile'
